import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CurrencyDigitShortcutPipeInput } from 'src/app/pipes/pipes';
import { TranslatedCurrencyDigitInputs, TranslatedCurrencyYearInputs } from 'src/app/pipes/translated-currency-input-keys';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ApiModelPortfolioChartOut, ApiModelPortfolioOut, ClientPortfolioEntryFeeCalcInput, EntryFeeCalculationWithAllRatiosOut, FrameworkContractOut, InvestmentFrequency, ModelPortfolioChartInput, NewPortfolioInput, UpdatePortfolioV2Input } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { UserService } from 'src/app/services/user/user.service';
import { CurrencyCodesEnum, DevelopmentChartData, InstrumentChartCardInput, ModelStrategyInstrumentOut } from 'src/app/shared/charts/charts';
import { availableInvestmentPeriods, defaultRiskLevels, maxInvestmentLength, newPortfolioConfig } from 'src/app/shared/constants';
import { SliderInputOptions } from 'src/app/shared/slider-input/slider-input';
import { distinctUntilChanged, debounceTime, take } from 'rxjs/operators';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ConfigService } from 'src/app/services/json-config.service';
import { EnvironmentKeys, riscScoreVideos } from 'src/environments/environment-keys';
import { adjustDevelopmentTimeLabels, AvailableCurrencySymbols, getCurrencyFromCode, isInputError, parseTimestamps, riskNameFromNumber } from 'src/app/shared/functions';
import { investmentAlreadyExistError } from 'src/app/shared/functions-angular';
import { LoggedUser } from 'src/app/services/authentication/authentication';
import Decimal from 'decimal.js';
import { VideoDetail } from '../user/videos/videos';
import { Subscription } from 'rxjs';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { SignalsService } from 'src/app/services/signals.service';
import { EntryFeeCalcService } from 'src/app/shared/entry-fee-calc/entry-fee-calc.service';
import { trigger } from '@angular/animations';
import { fadeInOutAnimation } from 'src/app/shared/animations-angular';
import { RadiosKeyValue } from 'src/app/shared/radio-input-group/radio-input-group';
import { Location } from '@angular/common';

interface SavedPortfolioInvestParams {
  initialDeposit: number;
  regularDeposit: number;
  weeksBetweenInvestments: RadiosKeyValue;
  totalYearsToInvest: number;
}

interface PortfolioForm {
  name: FormControl<string>;
  oneTimeDeposit: FormControl<number>;
  regularDeposit: FormControl<number>;
  timeOfInvestment: FormControl<number>;
  riskProfile: FormControl<number>;
  depositRegularity: FormControl<RadiosKeyValue>;
  dip: FormControl<FrameworkContractOut>
}

@Component({
  templateUrl: './new-portfolio.component.html',
  styleUrls: ['./new-portfolio.component.scss'],
  providers: [EntryFeeCalcService],
  animations: [trigger('fadeInOut', fadeInOutAnimation)],
  standalone: false
})
export class NewPortfolioComponent implements OnInit, OnDestroy {
  public EnvironmentKeys = EnvironmentKeys;
  public maxInvestmentLength = maxInvestmentLength;

  public instrumentChart: InstrumentChartCardInput = {
    chartData: [],
    id: 0,
  };

  public editId: number | null = null;
  public editPortfolioName: string | null = null;
  public savedInvestmentParams: SavedPortfolioInvestParams | null = null;

  private dipConfirmForm!: UntypedFormGroup;
  private dipConfirmFormSubscription?: Subscription;
  private creationConfirmSubscription?: Subscription;
  private vatConfirmSubscription?: Subscription;

  public dipAvailable = false;

  public get bothInvestmentsZero(): boolean {
    return Decimal.sum(this.modelChartForm.value['oneTimeDeposit'], this.modelChartForm.value['regularDeposit']).isZero();
  }

  public developmentChartLabels: number[] = [];

  public developmentChartData: DevelopmentChartData = {
    optimisticScenario: [],
    probableScenario: [],
    pessimisticScenario: [],
    deposits: [],
  };

  private entryFeeToDisplaySub: Subscription | null = null;
  public entryFeeToDisplay: EntryFeeCalculationWithAllRatiosOut | null = null;

  public yearsShortcutsOptions: CurrencyDigitShortcutPipeInput = {
    units: TranslatedCurrencyYearInputs
  }

  public digitShortcutsOptions: CurrencyDigitShortcutPipeInput = {
    digitShortcuts: TranslatedCurrencyDigitInputs
  }

  public availableInvestmentPeriods: RadiosKeyValue[] = availableInvestmentPeriods;

  public sliderOptions: SliderInputOptions = {
    step: 1,
    minValue: 1,
    maxValue: this.loggedUser.maxRiskScore,
    riskLevels: defaultRiskLevels,
  };

  private get loggedUser(): LoggedUser {
    if(!this.userService.loggedUser || this.userService.loggedUser === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedUser!;
  }

  public modelChartForm!: UntypedFormGroup;

  private formSubscription?: Subscription;

  public preloaders = {
    createPortfolio: false,
    modelChart: true,
    modelPortfolio: true,
    editDataFetching: false,
    editPortfolio: false,
  };

  public apiErrors = {
    modelChart: false,
    modelPortfolio: false,
  };

  public availableContracts: FrameworkContractOut[] = [];

  private get initialContract(): FrameworkContractOut {
    return this.availableContracts.find(contract => !contract.dip) ?? this.availableContracts[0];
  }

  public previewVideo: VideoDetail = {
    url: '',
  };

  public constructor(
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    public modalService: ModalService,
    public userService: UserService,
    private router: Router,
    private loggerService: LoggerService,
    public configService: ConfigService,
    private route: ActivatedRoute,
    private signalsService: SignalsService,
    public entryFeeCalcService: EntryFeeCalcService,
    private location: Location,
  ) { }

  public ngOnInit(): void {
    this.availableContracts.push(...this.userService.loggedUser!.validContracts.filter(contract => !contract.inLiquidation));
    this.dipAvailable = this.availableContracts.some(contract => contract.dip);
    // this.preloaders.editDataFetching = true;
    this.route.paramMap.pipe(take(1)).subscribe((params: ParamMap) => {
      const editId = params.get('portfolioId');
      if(editId && this.router.url.includes('edit')) {
        this.preloaders.editDataFetching = true;
        this.editId = parseInt(editId);
        this.restApiService.getPortfoliosAndStrategies().subscribe({
            error: err => this.initEditError(err),
            next: res => this.initEdit(res),
        });
      } else {
        this.initForm();
      }
    });

    this.entryFeeToDisplaySub = this.entryFeeCalcService.$entryFeeCalc
    .subscribe(data => this.entryFeeToDisplay = data);
  }

  private initForm(): void {
    const monthlyDefaultRegularityIndex: number = this.availableInvestmentPeriods.findIndex(period => period.key === 'monthly');

    this.modelChartForm = new FormGroup<PortfolioForm>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(newPortfolioConfig.investmentNameMaxLength),
        ],
      }),
      oneTimeDeposit: new FormControl(
        this.configService.getConfig(EnvironmentKeys.minimalInvest),
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      regularDeposit: new FormControl(
        this.configService.getConfig(EnvironmentKeys.minimalInvest),
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      timeOfInvestment: new FormControl(
        newPortfolioConfig.timeOfInvestmentInit,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      riskProfile: new FormControl(
        this.loggedUser.maxRiskScore,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      depositRegularity: new FormControl<RadiosKeyValue>(
        this.availableInvestmentPeriods[monthlyDefaultRegularityIndex],
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      dip: new FormControl<FrameworkContractOut>(
        this.initialContract,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
    });

    this.initFormSub();
  }

  private initEdit(portfolios: ExpectedOwnedStrategy[]): void {
    this.preloaders.editDataFetching = true;

    const portfolioData = portfolios.find(investment => investment.portfolioId === this.editId);

    if(!portfolioData) {
      this.initEditError('Edit portfolio ID was not found in existing investments!');
    } else {
      this.editPortfolioName = portfolioData.name;
      const depositRegularity = this.availableInvestmentPeriods.find(period => +period.value === portfolioData.weeksBetweenInvestments);
      if (!depositRegularity)
        this.initEditError(
          'Edit portfolio has invalid weeks between investments!',
        );
      this.modelChartForm = new UntypedFormGroup({
        name: new UntypedFormControl(portfolioData.name, [Validators.required, Validators.minLength(3), Validators.maxLength(newPortfolioConfig.investmentNameMaxLength)]),
        oneTimeDeposit: new UntypedFormControl(portfolioData.initialDeposit),
        regularDeposit: new UntypedFormControl(portfolioData.regularDeposit),
        timeOfInvestment: new UntypedFormControl(portfolioData.totalYearsToInvest),
        riskProfile: new UntypedFormControl(portfolioData.riskScore),
        depositRegularity: new UntypedFormControl(depositRegularity),
        dip: new UntypedFormControl(this.initialContract),
      });
      this.savedInvestmentParams = {
        initialDeposit: portfolioData.initialDeposit,
        regularDeposit: portfolioData.regularDeposit,
        totalYearsToInvest: portfolioData.totalYearsToInvest,
        weeksBetweenInvestments: depositRegularity ?? this.availableInvestmentPeriods[0]
      }

      this.availableInvestmentPeriods = this.availableInvestmentPeriods.map(period => {
        const periodMutable = JSON.parse(JSON.stringify(period));
        if (portfolioData.weeksBetweenInvestments < +period.value) {
          periodMutable.disabled = true;
        }
        return periodMutable;
      });

      this.sliderOptions.maxValue =
        portfolioData.riskScore ?? this.loggedUser.maxRiskScore;

      this.initFormSub();
      this.preloaders.editDataFetching = false;
    }
  }

  private initEditError(error: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.addNotification({
      message: 'toastNotifications.messages.portfolioEditLoadError',
      type: 'warning',
      component: ToastNotificationComponent
    });
    this.preloaders.editDataFetching = false;
    this.router.navigate(['/portfolio/new']);
  }

  private initFormSub(): void {
    this.formSubscription = this.modelChartForm.valueChanges.pipe(
      debounceTime(70),
      distinctUntilChanged((prev, curr) =>
        prev.oneTimeDeposit === curr.oneTimeDeposit &&
        prev.regularDeposit === curr.regularDeposit &&
        prev.timeOfInvestment === curr.timeOfInvestment &&
        prev.riskProfile === curr.riskProfile &&
        prev.depositRegularity === curr.depositRegularity &&
        prev.dip === curr.dip
      ),
    ).subscribe(
      (changes: ClientPortfolioEntryFeeCalcInput) => {
        this.formUpdated(changes);
      },
    );
  }

  private handleModelChartError(error: unknown): void {
    this.preloaders.modelChart = false;
    this.apiErrors.modelChart = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    this.toastService.unknownApiError();
  }

  private handleModelChartData(data: ApiModelPortfolioChartOut): void {
    data = parseTimestamps(data);

    this.updateDevelopmentChartLabels(data);
    this.emptyDevelopmentChart();
    this.replaceDevelopmentChartData(data);

    this.preloaders.modelChart = false;
  }

  private updateDevelopmentChartLabels(data: ApiModelPortfolioChartOut) {
    const dates = data.pureInvestment.map(date => date.x);

    this.developmentChartLabels.length = 0;
    this.developmentChartLabels.push(...adjustDevelopmentTimeLabels(dates));
  }

  private replaceDevelopmentChartData(data: ApiModelPortfolioChartOut): void {
    this.developmentChartData['optimisticScenario'].push(...data.optimistic);
    this.developmentChartData['probableScenario'].push(...data.expected);
    this.developmentChartData['pessimisticScenario'].push(...data.pessimistic);
    this.developmentChartData['deposits'].push(...data.pureInvestment);
  }

  private emptyDevelopmentChart(): void {
    this.developmentChartData['optimisticScenario'].length = 0;
    this.developmentChartData['probableScenario'].length = 0;
    this.developmentChartData['pessimisticScenario'].length = 0;
    this.developmentChartData['deposits'].length = 0;
  }

  private handleModelPortfolioError(error: unknown): void {
    this.preloaders.modelPortfolio = false;
    this.apiErrors.modelPortfolio = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleModelPortfolioData(data: ApiModelPortfolioOut, changes: ModelPortfolioChartInput): void {
    this.instrumentChart.chartData.length = 0;
    this.instrumentChart.chartData.push(...data.instruments);
    this.preloaders.modelPortfolio = false;
    this.calculateEntryFee(data.instruments, changes);
  }

  private calculateEntryFee(instruments: ModelStrategyInstrumentOut[], changes: ModelPortfolioChartInput) {
    const isinToWeightMap = new Map<string, number>();
    instruments.forEach(instrument => {
      if (instrument.instrument.isin) {
        isinToWeightMap.set(instrument.instrument.isin, instrument.weight)
      }
    });
    if (isinToWeightMap.size) {
      this.entryFeeCalcService.calculateEntryFeeToDisplay({
        changes: {...changes, instrumentsWeights: isinToWeightMap} as ClientPortfolioEntryFeeCalcInput,
      }, this.editId ?? null);
    }
  }

  private formUpdated(changes: ModelPortfolioChartInput): void {
    if(isNaN(changes.regularDeposit) || isNaN(changes.oneTimeDeposit) || isNaN(changes.timeOfInvestment)) {
      return;
    }

    if(changes.riskProfile) {
      this.updateSelectedRiscVideo(changes.riskProfile);
    }

    this.updateModelStrategy(changes);
    this.updateModelPortfolioChart(changes);
  }

  private updateSelectedRiscVideo(riskProfile: number) {
    this.previewVideo = {
      url: this.configService.getConfig(riscScoreVideos[riskNameFromNumber(riskProfile) + 'RiscScoreVideo']),
    }
  }

  private updateModelPortfolioChart(changes: ModelPortfolioChartInput): void {
    this.preloaders.modelChart = true;
    this.apiErrors.modelChart = false;

    this.restApiService.modelPortfolioChart(changes).subscribe({
      error: this.handleModelChartError.bind(this),
      next: this.handleModelChartData.bind(this),
    });
  }

  private updateModelStrategy(changes: ModelPortfolioChartInput): void {
    this.preloaders.modelPortfolio = true;
    this.apiErrors.modelPortfolio = false;

    this.restApiService.getModelPortfolio(parseInt(changes.riskProfile as unknown as string)).subscribe({
      error: err => this.handleModelPortfolioError(err),
      next: res => this.handleModelPortfolioData(res, changes),
    });
  }

  public openModal(): void {
    this.modalService.openConsultModal();
  }

  private validateUpdatePortfolioForm(): boolean {
    let valid = true;
    if(this.modelChartForm.value['name']?.length < newPortfolioConfig.investmentNameMinLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooShort',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['name']?.length > newPortfolioConfig.investmentNameMaxLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooLong',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }
    return valid;
  }

  private validateNewPortfolioForm(): boolean {
    let valid = true;

    if(this.modelChartForm.value['name']?.length < newPortfolioConfig.investmentNameMinLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooShort',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['name']?.length > newPortfolioConfig.investmentNameMaxLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooLong',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.bothInvestmentsZero) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.bothInvestmentsZero',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if((this.modelChartForm.value['oneTimeDeposit'] + this.modelChartForm.value['regularDeposit']) < 0) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.negativeDeposit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['timeOfInvestment'] < 1) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.negativeTimeOfInvestment',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['timeOfInvestment'] > this.maxInvestmentLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.maxTimeOfInvestment',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['oneTimeDeposit'] > this.configService.getConfig(EnvironmentKeys.maximalInitialInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.initialDepositOverLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['oneTimeDeposit'] !== 0 && this.modelChartForm.value['oneTimeDeposit'] < this.configService.getConfig(EnvironmentKeys.minimalInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.initialDepositUnderLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['regularDeposit'] !== 0 && this.modelChartForm.value['regularDeposit'] < this.configService.getConfig(EnvironmentKeys.minimalInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.regularDepositUnderLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['regularDeposit'] > this.configService.getConfig(EnvironmentKeys.maximalRegularInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.regularDepositOverLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(isNaN(this.modelChartForm.value['regularDeposit']) || isNaN(this.modelChartForm.value['oneTimeDeposit']) || isNaN(this.modelChartForm.value['timeOfInvestment'])) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.NaNValue',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    return valid;
  }

  private initDipConfirmForm(): void {
    this.dipConfirmForm = new UntypedFormGroup({
      dip: new UntypedFormControl(false),
    });

    this.dipConfirmFormSubscription = this.dipConfirmForm.valueChanges.pipe(
      distinctUntilChanged(
        (prev, curr) => prev.dip === curr.dip)
      ).subscribe((changes) => {
        if(changes.dip) {
          this.createPortfolio(true);
        }
      }
    );
  }

  public createPortfolio(dipConfirmed?: true): void {
    if(!dipConfirmed && this.modelChartForm.value.dip.dip) {
      // secondary config if DIP is selected before posting
      this.initDipConfirmForm();
      const dipControl = this.dipConfirmForm.controls['dip'];

      if(dipControl.value === false) {
        this.modalService.openDipModal(dipControl, 'new-portfolio');
      }
      return;
    }

    this.modelChartForm.markAllAsTouched();
    this.modelChartForm.updateValueAndValidity();

    if(!this.validateNewPortfolioForm()) {
      return;
    }

    const newPortfolioData: NewPortfolioInput = {
      name: this.modelChartForm.value['name'],
      initialDeposit: this.modelChartForm.value['oneTimeDeposit'],
      regularDeposit: this.modelChartForm.value['regularDeposit'],
      totalYearsToInvest: this.modelChartForm.value['timeOfInvestment'],
      riskScore: parseInt(this.modelChartForm.value['riskProfile']),
      weeksBetweenInvestments: parseInt(this.modelChartForm.value['depositRegularity'].value),
      frameworkContractId: this.modelChartForm.value.dip.frameworkContractId,
    }

    if(newPortfolioData.regularDeposit >= this.configService.getConfig(EnvironmentKeys.limitForInvestmentCreateConfirm)) {

      // timeout used to peacefully close previous confirm modal todo - some better approach for modals chaining (1)
      setTimeout(() => {
        this.modalService.openNewInvestmentConfirm(newPortfolioData);
        this.creationConfirmSubscription?.unsubscribe();
        this.creationConfirmSubscription = this.signalsService.confirmNewPortfolioHighRegular$.subscribe(updatedPortfolio => {
          if(updatedPortfolio) {
            this.openVatConfirmModal(updatedPortfolio);
            this.creationConfirmSubscription?.unsubscribe();
          }

        });
      });
    } else {
      this.openVatConfirmModal(newPortfolioData);
    }

    if(this.dipConfirmFormSubscription) {
      this.dipConfirmFormSubscription.unsubscribe();
    }
  }

  private openVatConfirmModal(updatedPortfolio: NewPortfolioInput): void {
    // timeout used to peacefully close previous confirm modal todo - some better approach for modals chaining (2)
    setTimeout(() => {
      this.modalService.openPortfolioVatConfirm();
      this.vatConfirmSubscription?.unsubscribe();
      this.vatConfirmSubscription = this.signalsService.confirmNewPortfolioVat$.subscribe((vatConfirmed) => {
        if(vatConfirmed) {
          this.postNewPortfolio(updatedPortfolio);
          this.vatConfirmSubscription?.unsubscribe();
        }
      });
    });
  }

  private postNewPortfolio(updatedPortfolio: NewPortfolioInput) {
    this.preloaders.createPortfolio = true;
    this.restApiService.postPortfolio(updatedPortfolio).subscribe({
      next: this.portfolioCreated.bind(this),
      error: this.handleCreationError.bind(this),
    });

    if(this.creationConfirmSubscription) {
      this.creationConfirmSubscription.unsubscribe();
    }
  }

  public updatePortfolio(): void {
    if(!this.editId || !this.validateUpdatePortfolioForm()) {
      return;
    }
    this.preloaders.editPortfolio = true;
    const input: UpdatePortfolioV2Input = {
      newPortfolioName: this.modelChartForm.value['name'],
      initialDeposit: this.modelChartForm.value['oneTimeDeposit'],
      regularDeposit: this.modelChartForm.value['regularDeposit'],
      weeksBetweenInvestments: this.getWeeksBetweenInvestmentsEnumByNumber(+this.modelChartForm.value['depositRegularity'].value as 1 | 4 | 12),
      totalYearsToInvest: this.modelChartForm.value['timeOfInvestment']
    };
    this.restApiService
      .updateInvestmentV2(input, this.editId)
      .subscribe({
        next: this.portfolioUpdated.bind(this),
        error: this.portfolioUpdateError.bind(this),
      });
  }

  private getWeeksBetweenInvestmentsEnumByNumber(wbi: 1 | 4 | 12): InvestmentFrequency {
    const map: Record<1 | 4 | 12, InvestmentFrequency> = {
      1: InvestmentFrequency.WEEKLY,
      4: InvestmentFrequency.MONTHLY,
      12: InvestmentFrequency.QUARTERLY,
    };
    return map[wbi];
  }

  private portfolioUpdated(): void {
    this.preloaders.editPortfolio = false;
    this.toastService.addNotification({
      title: 'toastNotifications.headers.success',
      message: 'toastNotifications.messages.portfolioUpdated',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.router.navigate(['/investment-detail/' + this.editId]);
  }

  private portfolioUpdateError(error: unknown): void {
    this.preloaders.editPortfolio = false;
    if(isInputError(error, 404)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.portfolioNameAlreadyExist',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.loggerService.error(typeof error === 'function' ? error() : error);
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.portfolioUpdateFailed',
        type: 'warning',
        component: ToastNotificationComponent
      });
    }
  }

  private portfolioCreated(response: { portfolioId: number }): void {
    if (!response['portfolioId']) {
      this.handleCreationError('Unknown server response for Create Portfolio.');
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.success',
        message: 'toastNotifications.messages.portfolioCreated',
        type: 'success',
        component: ToastNotificationComponent
      });

      this.router.navigate(['/investment-detail', response['portfolioId']]);
    }
  }

  private handleCreationError(error: unknown): void {
    this.preloaders.createPortfolio = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    if(investmentAlreadyExistError(error)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.portfolioNameAlreadyExist',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.portfolioCreateFailed',
        type: 'warning',
        component: ToastNotificationComponent
      });
    }
  }

  public getCurrencyFromCode(currencyCode: keyof typeof CurrencyCodesEnum): AvailableCurrencySymbols {
    return getCurrencyFromCode(currencyCode);
  }

  public discardChanges(): void {
    this.location.back();
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.entryFeeToDisplaySub?.unsubscribe();
    this.dipConfirmFormSubscription?.unsubscribe();
    this.creationConfirmSubscription?.unsubscribe();
    this.vatConfirmSubscription?.unsubscribe();
  }
}
